// @ts-check
import { mapEntityConfigToTenantDomainOption } from '@/services/xverdict_config_mapper';
import {
  as_number,
  as_object,
  first_string,
  is_record,
  record_value,
  to_array,
  to_number_or_string,
  to_string
} from './tenant_configuration_editor_normalisers';

/**
 * @typedef {import('./tenant_configuration_editor_types').TenantProject} TenantProject
 * @typedef {import('./tenant_configuration_editor_types').TenantDomainOption} TenantDomainOption
 * @typedef {import('./tenant_configuration_editor_types').ProjectBinding} ProjectBinding
 */

/** @param {unknown} value @returns {TenantProject} */
export function normalize_project(value) {
  const project = as_object(value);
  const creator_value = record_value(project, 'creator');
  const project_type_value = record_value(project, 'project_type');
  const creator = as_object(creator_value);
  const project_type = as_object(project_type_value);
  return project_payload(project, creator_value, creator, project_type_value, project_type);
}

function project_payload(project, creator_value, creator, project_type_value, project_type) {
  const payload = Object.fromEntries([]);
  payload.id = as_number(record_value(project, 'id'));
  payload.name = to_string(record_value(project, 'name'));
  payload.project_name = to_string(record_value(project, 'project_name'));
  payload.project_description = to_string(record_value(project, 'project_description'));
  payload.creator = project_reference(creator_value, creator);
  payload.project_type = project_reference(project_type_value, project_type);
  payload.enable_compliance_task = record_value(project, 'enable_compliance_task') === true;
  return payload;
}

function project_reference(source_value, source) {
  return is_record(source_value) ? { id: as_number(record_value(source, 'id')) } : null;
}

/** @param {unknown} projects @returns {TenantProject[]} */
export function normalize_projects(projects) {
  return to_array(projects, normalize_project);
}

/** @param {unknown} value @returns {TenantDomainOption|null} */
export function normalize_domain_option(value) {
  const domain = as_object(value);
  const config_id = as_number(record_value(domain, 'config_id'));
  const key = to_number_or_string(record_value(domain, 'key'));
  if (!key || !config_id) return null;
  const option = Object.fromEntries([]);
  option.key = key;
  option.config_id = config_id;
  option.title = to_string(record_value(domain, 'title'));
  option.description = to_string(record_value(domain, 'description'));
  return option;
}

/** @param {unknown} value @returns {TenantDomainOption|null} */
export function mapped_domain_option(value) {
  return normalize_domain_option(mapEntityConfigToTenantDomainOption(value));
}

/** @param {TenantDomainOption|null} value @returns {value is TenantDomainOption} */
export function is_domain_option(value) {
  return value !== null;
}

/** @param {unknown} value @returns {ProjectBinding|null} */
export function normalize_project_binding(value) {
  const binding = as_object(value);
  const id = as_number(record_value(binding, 'id'));
  const entity_config = as_number(record_value(binding, 'entity_config'));
  if (!id && !entity_config) return null;
  const project_binding = Object.fromEntries([]);
  project_binding.id = id;
  project_binding.entity_config = entity_config;
  project_binding.is_active = record_value(binding, 'is_active') !== false;
  return project_binding;
}

/** @param {ProjectBinding|null} value @returns {value is ProjectBinding} */
export function is_project_binding(value) {
  return value !== null;
}

/**
 * @param {TenantProject[]} projects
 * @param {unknown} selected_project_id
 * @returns {TenantProject|null}
 */
export function selected_project(projects, selected_project_id) {
  const project_id = as_number(selected_project_id);
  if (!project_id) return null;
  return projects.find((item) => item.id === project_id) || null;
}

/** @param {TenantProject|null} project @returns {number|null} */
export function project_creator_id(project) {
  return project?.creator?.id || null;
}

/**
 * @param {ProjectBinding[]} bindings
 * @param {TenantDomainOption[]} domains
 * @returns {Array<string|number>}
 */
export function selected_domain_keys(bindings = [], domains = []) {
  return bindings
    .filter((binding) => binding.is_active)
    .map((binding) => domains.find((domain) => domain.config_id === binding.entity_config)?.key)
    .filter((key) => key !== undefined);
}

/** @param {unknown} description @returns {string} */
export function sector_from_description(description) {
  return first_string([description]).replace(/^Sector:\s*/i, '').trim();
}
